import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { environment } from '@environment';

import { Subject, debounceTime, lastValueFrom } from 'rxjs';
import { CustomisationGroupPipe } from '@app/shared/pipes/customisation-group.pipe';
import { AbsPipe } from '@app/shared/pipes/abs.pipe';
import { RupeePipe } from '@app/shared/pipes/rupee.pipe';
import { BrandProductFilterPipe } from '@shared/pipes/brand-product-filter.pipe';
import { NgTemplateOutlet, NgStyle, NgClass, DatePipe } from '@angular/common';
import { DeliverySupportComponent } from '../../../shared/components/delivery-support/delivery-support.component';
import { MatIconModule } from '@angular/material/icon';
import { CustomerRefundsApiResponse, MailInfo, Refund } from '@app/shared/models/api-response/customer-refunds-response';
import { CartService } from '@app/core/services/cart/cart.service';
import { OrderApiService } from '@app/core/http/order-api.service';
import { OrdersUtilService } from '@app/core/services/orders-util/orders-util.service';
import { GtmService } from '@app/core/services/analytics/gtm.service';
import { RefundInfoComponent } from '@app/modules/refund/components/refund-info/refund-info.component';
import { ToastrService } from '@app/core/services/notification/toastr.service';

@Component({
    selector: 'app-order-summary-dialog',
    templateUrl: './order-summary-dialog.component.html',
    styleUrls: ['./order-summary-dialog.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        MatIconModule,
        DeliverySupportComponent,
        NgTemplateOutlet,
        NgStyle,
        NgClass,
        DatePipe,
        BrandProductFilterPipe,
        RupeePipe,
        AbsPipe,
        CustomisationGroupPipe,
        RefundInfoComponent
    ]
})
export class OrderSummaryDialogComponent implements OnInit {
    order: any;
    orderTiming: any;
    brand_details: any;
    freeItemDetails: any;

    menuItems = [];

    downloadingFile = false;

    DEPLOY_URL: string;
    lottieUrl: string;
    PCDCSavingstext = '';
    activeBillDetails = '';

    mailInfo: MailInfo;
    refund: Refund = null;

    destroySubject$: Subject<void> = new Subject();
    downloadButton = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private cartService: CartService,
        private dialogRef: MatDialogRef<OrderSummaryDialogComponent>,
        private orderApiService: OrderApiService,
        private ordersUtil: OrdersUtilService,
        private gtmService: GtmService,
        private toastrService: ToastrService
    ) {
        const { order, refund, mailInfo } = data;
        this.order = order;
        this.refund = refund;
        this.mailInfo = mailInfo;

        this.DEPLOY_URL = environment.DEPLOY_URL;
        this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/cart`;

        this.order.currentStatus = this.order.currentStatus || this.order.order_status.status;
        this.PCDCSavingstext = this.ordersUtil.getPCDCtext(this.order.order_details);

        this.getOrderStatus();
        this.getFreeItemDetails();
        this.initializeBrandDetails(this.order.order_details.items);
    }

    ngOnInit() {
        const { currentStatus } = this.order;
        if (currentStatus === 'cancelled' || currentStatus === 'delivered') {
            if (this.refund) {
                return;
            }

            lastValueFrom(this.orderApiService.getRefundDetails(this.order.order_details.tracking_id))
                .then((response: CustomerRefundsApiResponse) => {
                    if (response.meta.code === 200) {
                        const { refunds, mail_info } = response.data;
                        if (refunds) {
                            this.refund = refunds[0];
                            this.mailInfo = mail_info;
                        }
                    }
                })
                .catch(err => {
                    console.log(err);
                });
        }

        this.downloadButton.pipe(debounceTime(600)).subscribe(() => {
            this.fetchInvoice();
        });
    }

    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    initializeBrandDetails(orderItems: any) {
        this.menuItems = orderItems.filter(data => data.type !== 'FreeItem');
        const details = this.menuItems.reduce((acc, currValue) => {
            if (!acc[currValue.brand_id]) {
                acc[currValue.brand_id] = {
                    brand_id: currValue.brand_id,
                    brand_name: currValue.brand_name
                };
            }
            return acc;
        }, {});

        this.brand_details = Array.from(Object.values(details));
    }

    repeatOrder() {
        this.cartService.repeatOrder(this.order);
        this.gtmService.logWebAnalyticsEvent('repeat_order', 'Order Summary Screen');
        this.close();
    }

    close(value?: string) {
        this.dialogRef.close(value);
    }

    toggleView(value: string) {
        if (this.activeBillDetails === value) {
            this.activeBillDetails = '';
        } else {
            this.activeBillDetails = value;
        }
    }

    getOrderStatus() {
        const status = { text: '', time: '' };
        if (this.order.currentStatus === 'cancelled') {
            status.text = 'Cancelled On';
            status.time = this.order.order_status.updated_at;
        } else if (this.order.currentStatus === 'delivered') {
            status.text = 'Delivered On';
            status.time = this.order.order_status.updated_at;
        } else if (this.order.order_details.order_time) {
            status.text = 'Scheduled For';
            status.time = this.order.order_details.order_time;
        } else {
            status.text = 'Ordered On';
            status.time = this.order.order_details.placed_at;
        }
        this.orderTiming = status;
    }

    getFreeItemDetails() {
        const { items } = this.order.order_details;
        const freeItem = items.filter(item => item.type === 'FreeItem').map(item => item.name);
        let freeItemLabel = '';
        for (let i = 0; i < freeItem.length; i++) {
            if (i === 0) {
                freeItemLabel += freeItem[i];
            } else if (i === freeItem.length - 1) {
                freeItemLabel += ' and ' + freeItem[i];
            } else {
                freeItemLabel += ' , ' + freeItem[i];
            }
        }
        this.freeItemDetails = {
            quantity: freeItem.length,
            label: freeItemLabel
        };
    }

    downloadInvoice() {
        this.downloadButton.next(); // Triggers debounce
    }

    fetchInvoice() {
        this.downloadingFile = true;
        this.orderApiService.downloadInvoice(this.order.order_details.encrypted_tracking_id).subscribe(
            (response: any) => {
                const file = new Blob([response], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                const a: any = document.createElement('a'); // used type `any` just to suppress the compilation warning
                a.style = 'display: none';
                a.href = fileURL;
                a.download = 'invoice_' + this.order.order_details.tracking_id + '.pdf';
                document.body.appendChild(a);
                a.click();
                this.downloadingFile = false;
            },
            err => {
                this.downloadingFile = false;
                this.toastrService.openSnackBar(err?.meta?.message, 'OKAY');
                this.gtmService.logEvent('js_erros', {
                    event_value: 'Invoice Downloading Failed'
                });
            }
        );
    }
}
